/**
 * Día/noche de la ready room — Fase 5g.3.
 *
 * La ready room solo tiene arte de día y de noche (sin tarde). El inline
 * script de `portaaviones.html` fija `data-time-of-day` antes del primer
 * paint. En runtime este módulo decide el comportamiento:
 *
 *  - Modo CICLO (URL `?cycle[=N]` o preferencia CRT `cycle-N`, de
 *    CONFIG → MODO → CICLO Xs): alterna día↔noche cada N segundos.
 *    La sala cicla día→tarde→noche; aquí, sin tarde, es día↔noche.
 *  - Si no hay ciclo: watcher horario que re-evalúa cada minuto, solo
 *    en modo auto (con override fijo `?time=`/pref la elección es
 *    estable y no hay watcher).
 */

const CHECK_INTERVAL_MS = 60_000;
const DAY_START_MIN = 6 * 60; // 06:00
const NIGHT_START_MIN = 19 * 60; // 19:00
const CYCLE_PREF_RE = /^cycle-(\d+)$/;

type ReadyRoomTime = 'day' | 'night';

/** Día entre 06:00 y 18:59; noche el resto. */
function fromClock(): ReadyRoomTime {
  const now = new Date();
  const min = now.getHours() * 60 + now.getMinutes();
  return min >= DAY_START_MIN && min < NIGHT_START_MIN ? 'day' : 'night';
}

/** Lee el campo `timeOfDay` crudo del JSON de preferencias del CRT. */
function rawTimeOfDayPref(): string {
  try {
    const raw = localStorage.getItem('51esc.crt');
    if (!raw) return 'auto';
    const parsed = JSON.parse(raw) as { timeOfDay?: unknown };
    return typeof parsed.timeOfDay === 'string' ? parsed.timeOfDay : 'auto';
  } catch {
    return 'auto';
  }
}

/**
 * Período del ciclo en ms si el modo CICLO está activo (URL `?cycle` o
 * preferencia CRT `cycle-N`), o `null` si no. La URL gana sobre la pref.
 */
function resolveCyclePeriodMs(): number | null {
  const params = new URLSearchParams(window.location.search);
  if (params.has('cycle')) {
    const raw = params.get('cycle');
    const n =
      raw && Number.isFinite(Number(raw)) && Number(raw) > 0
        ? Number(raw)
        : 30;
    return n * 1000;
  }
  const m = CYCLE_PREF_RE.exec(rawTimeOfDayPref());
  if (!m) return null;
  const secs = Number(m[1]);
  return Number.isFinite(secs) && secs > 0 ? secs * 1000 : null;
}

/** `true` si hay override fijo (URL `?time=` o pref day/dusk/night). */
function hasFixedOverride(): boolean {
  const urlT = new URLSearchParams(window.location.search).get('time');
  if (urlT === 'day' || urlT === 'night' || urlT === 'dusk') return true;
  const t = rawTimeOfDayPref();
  return t === 'day' || t === 'night' || t === 'dusk';
}

/**
 * Arranca la gestión día/noche de la ready room: el ciclador si el modo
 * CICLO está activo, el watcher horario en caso contrario.
 */
export function startReadyRoomTimeOfDay(): void {
  const cyclePeriodMs = resolveCyclePeriodMs();
  if (cyclePeriodMs !== null) {
    startCycle(cyclePeriodMs);
    return;
  }
  startWatch();
}

/**
 * Alterna `data-time-of-day` entre día y noche cada `periodMs`. Parte
 * del estado que dejó el inline script — el primer cambio se nota al
 * cumplirse el primer período.
 */
function startCycle(periodMs: number): void {
  const root = document.documentElement;
  window.setInterval(() => {
    root.dataset.timeOfDay =
      root.dataset.timeOfDay === 'day' ? 'night' : 'day';
  }, periodMs);
}

/** Re-evalúa la hora del día cada minuto (solo en modo auto). */
function startWatch(): void {
  if (hasFixedOverride()) return;
  const root = document.documentElement;
  const apply = (): void => {
    const next = fromClock();
    if (root.dataset.timeOfDay !== next) root.dataset.timeOfDay = next;
  };
  apply();
  window.setInterval(apply, CHECK_INTERVAL_MS);
}
